import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Zap, Mail, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { trackEvent } from "@/lib/analytics";

const MarketingFooter = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [honeypot, setHoneypot] = useState(""); // Hidden spam trap - bots fill this
  const [isLoading, setIsLoading] = useState(false);

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast({ title: "Invalid email", description: "Please enter a valid email address.", variant: "destructive" });
      return;
    }

    setIsLoading(true);
    
    try {
      // Call edge function via supabase.functions.invoke (no hardcoded URLs)
      const { data, error } = await supabase.functions.invoke('subscribe-email', {
        body: { 
          email: email.trim().toLowerCase(),
          honeypot,
          source: 'footer'
        }
      });

      if (error) {
        // Network or invocation error
        toast({ 
          title: "Error", 
          description: "Something went wrong. Please try again.", 
          variant: "destructive" 
        });
        return;
      }

      // Handle edge function response
      if (data?.error) {
        toast({ 
          title: "Error", 
          description: data.error, 
          variant: "destructive" 
        });
        return;
      }

      // Success cases
      if (data?.status === 'already_subscribed') {
        toast({ 
          title: "Already subscribed", 
          description: "This email is already on our list!" 
        });
      } else {
        trackEvent('email_subscribed', { source: 'footer' });
        toast({ 
          title: "Subscribed!", 
          description: "We'll notify you of updates." 
        });
      }
      
      setEmail("");

    } catch (error) {
      console.error('[MarketingFooter] Subscription error:', error);
      toast({ 
        title: "Error", 
        description: "Something went wrong. Please try again.", 
        variant: "destructive" 
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <footer className="border-t border-border py-8">
      <div className="container mx-auto px-4">
        <div className="flex flex-col gap-6">
          {/* Email signup */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <span className="text-sm text-muted-foreground">Get notified of updates:</span>
            <form onSubmit={handleSubscribe} className="flex gap-2">
              {/* Hidden honeypot field - bots fill this, humans don't see it */}
              <input
                type="text"
                name="website"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                style={{ position: 'absolute', left: '-9999px' }}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
              />
              <Input
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-48 sm:w-56 h-9"
                disabled={isLoading}
              />
              <Button type="submit" size="sm" disabled={isLoading}>
                {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
              </Button>
            </form>
          </div>

          {/* Footer content */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex flex-col items-center md:items-start gap-1">
              <div className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-primary" />
                <span className="font-semibold text-foreground">StudyForge</span>
              </div>
              <span className="text-xs text-muted-foreground italic">
                Made for consistency. Built for enjoyment.
              </span>
              <span className="text-xs text-muted-foreground">
                © 2025 StudyForge
              </span>
            </div>
            <div className="flex items-center gap-6 text-sm text-muted-foreground">
              <button onClick={() => navigate('/features')} className="hover:text-foreground transition-colors">Features</button>
              <button onClick={() => navigate('/pricing')} className="hover:text-foreground transition-colors">Pricing</button>
              <button onClick={() => navigate('/about')} className="hover:text-foreground transition-colors">About</button>
              <button onClick={() => navigate('/privacy-policy')} className="hover:text-foreground transition-colors">Privacy</button>
              <button onClick={() => navigate('/terms-of-service')} className="hover:text-foreground transition-colors">Terms</button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default MarketingFooter;
